import {
  handleInstantHeatOff,
  handleSnowSensorOff,
  handleSnowSensor,
  handleWindFactor,
  handleWindFactorOff,
  handleAddHeatingSchedule,
  handleReadyHeatingSchedule,
  handleClearHeatingSchedule,
  handleMachineOptionalConstantTempOff,
  handleOptionalConstantTempReady,
  handleInstantHeatReady,
  selectEssSwitch,
  essHandleUnselectAllProgram,
  essHandleSelectProgram,
} from 'shared/slice/essSwitchSlice';

import {
  tgsHandleInstantHeatOff,
  selectTgsSwitch,
  tgsHandleInstantHeat,
  tgsHandleSnowSensorOff,
  tgsHandleSnowSensor,
  tgsHandleWindFactor,
  tgsHandleWindFactorOff,
  tgsHandleAddHeatingSchedule,
  tgsHandleReadyHeatingSchedule,
  tgsHandleFanOnly,
  tgsHandleClearHeatingSchedule,
  tgsHandleUnselectAllProgram,
  tgsHandleSelectProgram,
  tgsDeactivateConflictMessage,
  tgsActivateConflictMessage,
  tgsSetDevicesConflicts,
} from 'shared/slice/tgsSwitchSlice';

import {
  tesHandleInstantHeatOff,
  selectTesSwitch,
  tesHandleInstantHeat,
  tesHandleSnowSensorOff,
  tesHandleSnowSensor,
  tesHandleOptionalConstantTempOff,
  tesHandleWindFactor,
  tesHandleWindFactorOff,
  tesHandleAddHeatingSchedule,
  tesHandleReadyHeatingSchedule,
  tesHandleClearHeatingSchedule,
  tesHandleUnselectAllProgram,
  tesHandleSelectProgram,
  tesDeactivateConflictMessage,
  tesActivateConflictMessage,
  tesSetDevicesConflicts,
  tesHandleOptionalConstantTempIsReady,
} from 'shared/slice/tesSwitchSlice';

import { postEssCommand, postTgsCommand, postTesCommand } from 'features/masterControl/services/sendCommand.service';

export const CONTROL_BOX_CONFIG = {
  ess: {
    hasOptionalConstant: true,
    hasFanOnly: false,
    hasConflictMessage: false,
    deviceType: 'ESS',
    postCommand: postEssCommand,
    conflictCheckTarget: null,
    switchSelector: selectEssSwitch,
    flatSwitchKey: 'flatEssSwitch',
    conflictStrings: null,
    actions: {
      instantHeatReady: handleInstantHeatReady,
      instantHeatOff: handleInstantHeatOff,
      snowSensor: handleSnowSensor,
      snowSensorOff: handleSnowSensorOff,
      windFactor: handleWindFactor,
      windFactorOff: handleWindFactorOff,
      optionalConstantTempReady: handleOptionalConstantTempReady,
      optionalConstantTempOff: handleMachineOptionalConstantTempOff,
      addHeatingSchedule: handleAddHeatingSchedule,
      readyHeatingSchedule: handleReadyHeatingSchedule,
      clearHeatingSchedule: handleClearHeatingSchedule,
      unselectAllProgram: essHandleUnselectAllProgram,
      selectProgram: essHandleSelectProgram,
      activateConflictMessage: null,
      deactivateConflictMessage: null,
      setDevicesConflicts: null,
      fanOnly: null,
    },
  },
  tgs: {
    hasOptionalConstant: false,
    hasFanOnly: true,
    hasConflictMessage: true,
    deviceType: 'TGS',
    postCommand: postTgsCommand,
    conflictCheckTarget: 'electrical',
    switchSelector: selectTgsSwitch,
    flatSwitchKey: 'flatTgsSwitch',
    conflictStrings: {
      currentSwitch: 'tes-typhoon electric system',
      desiredSwitch: 'tgs-typhoon gas system',
      systemTarget: 'gas',
    },
    actions: {
      instantHeatReady: tgsHandleInstantHeat,
      instantHeatOff: tgsHandleInstantHeatOff,
      snowSensor: tgsHandleSnowSensor,
      snowSensorOff: tgsHandleSnowSensorOff,
      windFactor: tgsHandleWindFactor,
      windFactorOff: tgsHandleWindFactorOff,
      optionalConstantTempReady: null,
      optionalConstantTempOff: null,
      addHeatingSchedule: tgsHandleAddHeatingSchedule,
      readyHeatingSchedule: tgsHandleReadyHeatingSchedule,
      clearHeatingSchedule: tgsHandleClearHeatingSchedule,
      unselectAllProgram: tgsHandleUnselectAllProgram,
      selectProgram: tgsHandleSelectProgram,
      activateConflictMessage: tgsActivateConflictMessage,
      deactivateConflictMessage: tgsDeactivateConflictMessage,
      setDevicesConflicts: tgsSetDevicesConflicts,
      fanOnly: tgsHandleFanOnly,
    },
  },
  tes: {
    hasOptionalConstant: true,
    hasFanOnly: false,
    hasConflictMessage: true,
    deviceType: 'TES',
    postCommand: postTesCommand,
    conflictCheckTarget: 'gas',
    switchSelector: selectTesSwitch,
    flatSwitchKey: 'flatTesSwitch',
    conflictStrings: {
      currentSwitch: 'tgs-typhoon gas system',
      desiredSwitch: 'tgs-typhoon electric system',
      systemTarget: 'electric',
    },
    actions: {
      instantHeatReady: tesHandleInstantHeat,
      instantHeatOff: tesHandleInstantHeatOff,
      snowSensor: tesHandleSnowSensor,
      snowSensorOff: tesHandleSnowSensorOff,
      windFactor: tesHandleWindFactor,
      windFactorOff: tesHandleWindFactorOff,
      optionalConstantTempReady: tesHandleOptionalConstantTempIsReady,
      optionalConstantTempOff: tesHandleOptionalConstantTempOff,
      addHeatingSchedule: tesHandleAddHeatingSchedule,
      readyHeatingSchedule: tesHandleReadyHeatingSchedule,
      clearHeatingSchedule: tesHandleClearHeatingSchedule,
      unselectAllProgram: tesHandleUnselectAllProgram,
      selectProgram: tesHandleSelectProgram,
      activateConflictMessage: tesActivateConflictMessage,
      deactivateConflictMessage: tesDeactivateConflictMessage,
      setDevicesConflicts: tesSetDevicesConflicts,
      fanOnly: null,
    },
  },
};

const MOBILE_PROGRAMS = {
  ess: [
    { key: 'instantHeat', icon: '/images/logo-instantHeat.svg' },
    { key: 'snowSensor', icon: '/images/logo-snowSensor.svg' },
    { key: 'optionalConstantTemp', icon: '/images/logo-constantTemp.svg' },
    { key: 'heatingSchedule', icon: '/images/logo-schedule.svg' },
    { key: 'windFactor', icon: '/images/logo-windFactor.svg' },
  ],
  tgs: [
    { key: 'instantHeat', icon: '/images/logo-instantHeat.svg' },
    { key: 'fanOnly', icon: '/images/tgs-fanOnly.svg' },
    { key: 'snowSensor', icon: '/images/logo-snowSensor.svg' },
    { key: 'heatingSchedule', icon: '/images/logo-schedule.svg' },
    { key: 'windFactor', icon: '/images/logo-windFactor.svg' },
  ],
  tes: [
    { key: 'instantHeat', icon: '/images/logo-instantHeat.svg' },
    { key: 'snowSensor', icon: '/images/logo-snowSensor.svg' },
    { key: 'optionalConstantTemp', icon: '/images/logo-constantTemp.svg' },
    { key: 'heatingSchedule', icon: '/images/logo-schedule.svg' },
    { key: 'windFactor', icon: '/images/logo-windFactor.svg' },
  ],
};

export { MOBILE_PROGRAMS };
